import React, { useEffect, useRef, useState } from "react";
import { setRandomBackground } from "../helpers/backgroundHelpers";
import { ChatMessage } from "../models/chatMessage";
import { generateUUID } from "three/src/math/MathUtils";

let storedUserId: string | null = null;
if (typeof window !== "undefined") {
  storedUserId = localStorage.getItem("chatUserId");
  if (!storedUserId) {
    storedUserId = generateUUID();
    localStorage.setItem("chatUserId", storedUserId);
  }
}

const Chat: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [userId] = useState<string>(storedUserId || generateUUID());
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  const connectChatServer = () => {
    const socket = new WebSocket("wss://robyn-chat-api.azurewebsites.net/");

    socket.onopen = () => {
      console.log("Connected to chat server");
    };

    socket.onmessage = (event) => {
      console.log("Received message:", event.data);
      const message: ChatMessage = JSON.parse(event.data);
      setMessages((prevMessages) => [...prevMessages, message]);
    };

    socket.onclose = () => {
      console.log("Disconnected from chat server");
    };

    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
    };
    setWs(socket);

    return socket;
  };

  window.onkeydown = (e) => {
    if (e.key === "Enter" && inputText.trim() !== "") {
      sendMessage(inputText);
      setInputText("");
    }
  };

  useEffect(() => {
    setRandomBackground();
    let socket = connectChatServer();

    return () => {
      socket.close();
    };
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  const sendMessage = (text: string) => {
    const message: ChatMessage = {
      type: "chat",
      user: userName || `anonymous-${userId.substring(0, 8)}`,
      text,
      userId,
    };

    if (ws?.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket is not open. Reconnecting...");
      connectChatServer();
    }

    ws?.send(JSON.stringify(message));
  };

  return (
    <div
      className="chat-page"
      style={{
        padding: "2rem 1.25rem 4rem",
        maxWidth: 1000,
        margin: "0 auto",
        color: "#fff",
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        lineHeight: 1.45,
      }}
    >
      <header style={{ marginBottom: "2rem" }}>
        <h1 style={{ margin: 0, fontSize: "2.4rem", letterSpacing: "0.5px" }}>
          Chat
        </h1>
        <p style={{ opacity: 0.8, margin: "0.5rem 0 0" }}>
          Chat with others who happen to be here!
        </p>
      </header>

      <section aria-labelledby="chat-messages-heading">
        <h2 id="chat-messages-heading" style={{ fontSize: "1.4rem" }}>
          messages
        </h2>
        <div
          className="chat-messages"
          ref={messagesContainerRef}
          style={{
            marginTop: "1rem",
            maxHeight: "400px",
            overflowY: "auto",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "8px",
            padding: "1rem",
            background: "rgba(0,0,0,0.2)",
          }}
        >
          {messages.length === 0 ? (
            <p style={{ opacity: 0.7 }}>No messages yet.</p>
          ) : (
            messages.map((msg, index) => (
              <div key={index} style={{ marginBottom: "0.75rem" }}>
                <strong>{msg.user || "Anonymous"}:</strong> {msg.text}
                <br />
                <small style={{ opacity: 0.6 }}>
                  {msg.timestamp
                    ? new Date(msg.timestamp).toLocaleTimeString()
                    : ""}
                </small>
              </div>
            ))
          )}
        </div>
      </section>
      <section>
        <h2 style={{ fontSize: "1.4rem", marginTop: "2rem" }}>
          send a message
        </h2>
        <div className="chat-form">
          <input
            className="glass-input glass-input-name"
            type="text"
            placeholder="name (optional)"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
          />
          <input
            className="glass-input glass-input-message"
            type="text"
            placeholder="type your message here..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
          />
          <button
            className="chat-send-btn"
            onClick={() => {
              sendMessage(inputText);
              setInputText("");
            }}
          >
            Send
          </button>
        </div>
      </section>
    </div>
  );
};

export default Chat;
